import { isMobile } from '../../utils'
import {
  HOTBAR_ITEMS,
  ITEMS,
  InventorySlot,
  ItemId,
  getBlockTypeByItemId
} from '../../items'

type CraftRecipe = {
  pattern: (ItemId | null)[]
  result: Exclude<InventorySlot, null>
}

type DragSource =
  | {
      type: 'inventory'
      index: number
    }
  | {
      type: 'craft'
      index: number
    }
  | {
      type: 'result'
    }
  | {
      type: 'furnace'
      slot: 'input' | 'fuel' | 'result'
    }

const CRAFT_RECIPES: CraftRecipe[] = [
  {
    pattern: ['tree', null, null, null, null, null, null, null, null],
    result: { itemId: 'wood', count: 4 }
  },
  {
    pattern: ['jungleTree', null, null, null, null, null, null, null, null],
    result: { itemId: 'jungleWood', count: 4 }
  },
  {
    pattern: ['wood', null, null, 'wood', null, null, null, null, null],
    result: { itemId: 'stick', count: 4 }
  },
  {
    pattern: ['jungleWood', null, null, 'jungleWood', null, null, null, null, null],
    result: { itemId: 'stick', count: 4 }
  },
  {
    pattern: [
      'wood',
      'wood',
      'wood',
      'wood',
      null,
      'wood',
      'wood',
      'wood',
      'wood'
    ],
    result: { itemId: 'chest', count: 1 }
  },
  {
    pattern: [
      'jungleWood',
      'jungleWood',
      'jungleWood',
      'jungleWood',
      null,
      'jungleWood',
      'jungleWood',
      'jungleWood',
      'jungleWood'
    ],
    result: { itemId: 'chest', count: 1 }
  }
]

const FURNACE_RECIPES: Partial<Record<ItemId, ItemId>> = {
  beef: 'cookedBeef',
  rottenFlesh: 'cookedMeat',
  ironOre: 'ironIngot'
}

const FURNACE_FUELS: Partial<Record<ItemId, number>> = {
  coal: 8,
  tree: 2,
  jungleTree: 2,
  wood: 1,
  jungleWood: 1
}

export default class Bag {
  constructor(onToggle?: (open: boolean) => void) {
    this.onToggle = onToggle
    if (isMobile) return

    this.bag.className = 'bag'
    this.panel.className = 'inventory-panel hidden'
    this.hand.className = 'held-item'
    this.bag.classList.add('hidden')
    this.hand.classList.add('hidden')
    this.drops.className = 'drop-feed hidden'

    for (let i = 0; i < HOTBAR_ITEMS.length; i++) {
      this.slots[27 + i] = {
        itemId: HOTBAR_ITEMS[i],
        count: 64
      }
    }
    this.slots[0] = { itemId: 'furnace', count: 1 }
    this.slots[1] = { itemId: 'coal', count: 16 }
    this.slots[2] = { itemId: 'ironOre', count: 8 }
    this.slots[3] = { itemId: 'beef', count: 8 }

    this.renderHotbar()
    this.renderInventory()
    this.renderHand()

    document.body.appendChild(this.bag)
    document.body.appendChild(this.panel)
    document.body.appendChild(this.hand)
    document.body.appendChild(this.drops)

    document.body.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'e' && !e.repeat) {
        this.toggleInventory()
      }

      if (isNaN(parseInt(e.key)) || e.key === '0') {
        return
      }

      this.current = parseInt(e.key) - 1
      this.renderHotbar()
      this.renderHand()
    })

    document.body.addEventListener('wheel', (e: WheelEvent) => {
      if (!this.wheelGap) {
        this.wheelGap = true
        setTimeout(() => {
          this.wheelGap = false
        }, 100)
        if (e.deltaY > 0) {
          this.current++
          this.current > 8 && (this.current = 0)
        } else if (e.deltaY < 0) {
          this.current--
          this.current < 0 && (this.current = 8)
        }
        this.renderHotbar()
        this.renderHand()
      }
    })
  }
  wheelGap = false
  current = 0
  slots: InventorySlot[] = new Array(36).fill(null)
  craftSlots: InventorySlot[] = new Array(9).fill(null)
  craftResult: InventorySlot = null
  furnaceInput: InventorySlot = null
  furnaceFuel: InventorySlot = null
  furnaceResult: InventorySlot = null
  isInventoryOpen = false
  isActive = false
  suppressToggle = false
  onToggle?: (open: boolean) => void
  dragSource?: DragSource

  bag = document.createElement('div')
  panel = document.createElement('div')
  hand = document.createElement('div')
  drops = document.createElement('div')

  addItem(itemId: ItemId, count = 1) {
    for (const slot of this.slots) {
      if (slot?.itemId === itemId && slot.count < 64) {
        const accepted = Math.min(64 - slot.count, count)
        slot.count += accepted
        count -= accepted
        if (count === 0) {
          this.renderAll()
          return true
        }
      }
    }

    for (let i = 0; i < this.slots.length; i++) {
      if (!this.slots[i]) {
        const accepted = Math.min(64, count)
        this.slots[i] = { itemId, count: accepted }
        count -= accepted
        if (count === 0) {
          this.renderAll()
          return true
        }
      }
    }

    this.renderAll()
    return count === 0
  }

  getHoldingBlock() {
    const slot = this.slots[27 + this.current]
    const blockType = slot ? getBlockTypeByItemId(slot.itemId) : undefined
    return blockType
  }

  getHoldingItemId() {
    return this.slots[27 + this.current]?.itemId
  }

  consumeHoldingBlock() {
    const slotIndex = 27 + this.current
    const slot = this.slots[slotIndex]
    const blockType = slot ? getBlockTypeByItemId(slot.itemId) : undefined
    if (!slot || blockType === undefined) {
      return false
    }

    slot.count--
    if (slot.count <= 0) {
      this.slots[slotIndex] = null
    }
    this.renderAll()
    return true
  }

  setActive(active: boolean) {
    this.isActive = active
    this.bag.classList.toggle('hidden', !active)
    this.hand.classList.toggle('hidden', !active || !this.getHoldingItemId())
    if (!active && this.isInventoryOpen) {
      this.isInventoryOpen = false
      this.panel.classList.add('hidden')
      this.onToggle?.(false)
    }
  }

  toggleInventory() {
    if (!this.isActive || this.suppressToggle) {
      return
    }
    this.isInventoryOpen = !this.isInventoryOpen
    this.panel.classList.toggle('hidden', !this.isInventoryOpen)
    this.onToggle?.(this.isInventoryOpen)
  }

  // re-render hotbar / inventory / hand from the outside (e.g. chest transfers)
  refresh = () => {
    this.renderAll()
  }

  playUseAnimation(type: 'place' | 'mine' | 'attack') {
    this.hand.classList.remove('use-place', 'use-mine', 'use-attack')
    window.setTimeout(() => {
      this.hand.classList.add(`use-${type}`)
      window.setTimeout(() => {
        this.hand.classList.remove(`use-${type}`)
      }, 260)
    }, 0)
  }

  showDrop(itemId: ItemId, count = 1) {
    const item = ITEMS[itemId]
    this.drops.classList.remove('hidden')
    const row = document.createElement('div')
    row.className = 'drop-toast'
    row.innerHTML = `<img src="${item.icon}" alt="${item.name}" /> +${count} ${item.name}`
    this.drops.appendChild(row)
    setTimeout(() => {
      row.remove()
      if (!this.drops.children.length) {
        this.drops.classList.add('hidden')
      }
    }, 1400)
  }


  private renderAll() {
    this.renderHotbar()
    this.renderInventory()
    this.renderHand()
  }

  private renderHotbar() {
    this.bag.innerHTML = ''
    for (let i = 0; i < 9; i++) {
      const index = 27 + i
      const slot = this.createSlot(
        this.slots[index],
        `item${i === this.current ? ' selected' : ''}`
      )
      this.bindInventoryDrag(slot, index)
      this.bag.appendChild(slot)
    }
  }

  private renderHand() {
    const slot = this.slots[27 + this.current]
    this.hand.innerHTML = ''
    if (!slot) {
      this.hand.classList.add('hidden')
      return
    }

    const item = ITEMS[slot.itemId]
    const img = document.createElement('img')
    img.src = item.icon
    img.alt = item.name
    this.hand.appendChild(img)
    this.hand.classList.toggle('hidden', !this.isActive)
  }

  private renderInventory() {
    this.panel.innerHTML = ''

    const title = document.createElement('div')
    title.className = 'inventory-title'
    title.innerHTML = '背包'
    this.panel.appendChild(title)

    const craft = document.createElement('div')
    craft.className = 'craft-area'

    const craftGrid = document.createElement('div')
    craftGrid.className = 'craft-grid'
    for (let i = 0; i < this.craftSlots.length; i++) {
      const slot = this.createSlot(this.craftSlots[i], 'item craft-slot')
      this.bindCraftDrag(slot, i)
      craftGrid.appendChild(slot)
    }

    const arrow = document.createElement('div')
    arrow.className = 'craft-arrow'
    arrow.innerHTML = '=>'

    const result = this.createSlot(this.craftResult, 'item result-slot')
    result.addEventListener('click', () => this.takeCraftResult())
    this.bindResultDrag(result)

    const craftLabel = document.createElement('div')
    craftLabel.className = 'craft-label'
    craftLabel.innerHTML = '九格合成'

    craft.appendChild(craftLabel)
    craft.appendChild(craftGrid)
    craft.appendChild(arrow)
    craft.appendChild(result)
    this.panel.appendChild(craft)

    const furnace = document.createElement('div')
    furnace.className = 'furnace-area'
    const furnaceLabel = document.createElement('div')
    furnaceLabel.className = 'craft-label'
    furnaceLabel.innerHTML = '熔炉'
    const input = this.createSlot(this.furnaceInput, 'item furnace-slot')
    this.bindFurnaceDrag(input, 'input')
    const fuel = this.createSlot(this.furnaceFuel, 'item furnace-slot')
    this.bindFurnaceDrag(fuel, 'fuel')
    const smeltButton = document.createElement('button')
    smeltButton.className = 'button furnace-button'
    smeltButton.innerHTML = '烧制'
    smeltButton.addEventListener('click', () => this.smelt())
    const furnaceResult = this.createSlot(this.furnaceResult, 'item result-slot')
    this.bindFurnaceDrag(furnaceResult, 'result')
    furnace.appendChild(furnaceLabel)
    furnace.appendChild(input)
    furnace.appendChild(fuel)
    furnace.appendChild(smeltButton)
    furnace.appendChild(furnaceResult)
    this.panel.appendChild(furnace)

    const grid = document.createElement('div')
    grid.className = 'inventory-grid'
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.createSlot(this.slots[i], 'item inventory-slot')
      this.bindInventoryDrag(slot, i)
      grid.appendChild(slot)
    }
    this.panel.appendChild(grid)
  }

  private createSlot(slot: InventorySlot, className: string) {
    const item = document.createElement('div')
    item.className = className

    if (slot) {
      item.draggable = true
      const definition = ITEMS[slot.itemId]
      const img = document.createElement('img')
      img.className = 'icon'
      img.alt = definition.name
      img.title = definition.name
      img.src = definition.icon
      item.appendChild(img)

      const count = document.createElement('span')
      count.className = 'item-count'
      count.innerHTML = `${slot.count}`
      item.appendChild(count)
    }

    return item
  }

  private bindInventoryDrag(element: HTMLElement, index: number) {
    element.addEventListener('dragstart', e => {
      if (!this.slots[index]) {
        e.preventDefault()
        return
      }
      this.dragSource = { type: 'inventory', index }
      e.dataTransfer?.setData('text/plain', `${index}`)
    })
    element.addEventListener('dragover', e => {
      e.preventDefault()
    })
    element.addEventListener('drop', e => {
      e.preventDefault()
      this.moveDraggedItem({ type: 'inventory', index })
    })
  }

  private bindCraftDrag(element: HTMLElement, index: number) {
    element.addEventListener('dragstart', e => {
      if (!this.craftSlots[index]) {
        e.preventDefault()
        return
      }
      this.dragSource = { type: 'craft', index }
      e.dataTransfer?.setData('text/plain', `craft-${index}`)
    })
    element.addEventListener('dragover', e => {
      e.preventDefault()
    })
    element.addEventListener('drop', e => {
      e.preventDefault()
      this.moveDraggedItem({ type: 'craft', index })
    })
  }

  private moveDraggedItem(target: DragSource) {
    if (!this.dragSource) {
      return
    }
    if (target.type === 'result') {
      return
    }

    const source = this.dragSource
    const sourceSlot = this.getSlot(source)
    if (source.type === 'result') {
      if (!sourceSlot || target.type === 'craft' || !this.hasCraftIngredients()) {
        return
      }
      const targetSlot = this.getSlot(target)
      if (targetSlot && targetSlot.itemId !== sourceSlot.itemId) {
        return
      }
      this.consumeCraftIngredients()
      if (targetSlot) {
        targetSlot.count = Math.min(64, targetSlot.count + sourceSlot.count)
      } else {
        this.setSlot(target, { ...sourceSlot })
      }
      this.dragSource = undefined
      this.updateCraftResult()
      this.renderAll()
      return
    }
    if (source.type === 'furnace' && source.slot === 'result') {
      if (!sourceSlot || target.type === 'craft') {
        return
      }
      const targetSlot = this.getSlot(target)
      if (targetSlot && targetSlot.itemId !== sourceSlot.itemId) {
        return
      }
      if (targetSlot) {
        targetSlot.count = Math.min(64, targetSlot.count + sourceSlot.count)
      } else {
        this.setSlot(target, sourceSlot)
      }
      this.furnaceResult = null
      this.dragSource = undefined
      this.renderAll()
      return
    }

    const targetSlot = this.getSlot(target)
    this.setSlot(source, targetSlot)
    this.setSlot(target, sourceSlot)
    this.dragSource = undefined
    this.updateCraftResult()
    this.renderAll()
  }

  private getSlot(source: DragSource) {
    if (source.type === 'result') {
      return this.craftResult
    }
    if (source.type === 'furnace') {
      return source.slot === 'input'
        ? this.furnaceInput
        : source.slot === 'fuel'
          ? this.furnaceFuel
          : this.furnaceResult
    }
    return source.type === 'inventory'
      ? this.slots[source.index]
      : this.craftSlots[source.index]
  }

  private setSlot(source: DragSource, slot: InventorySlot) {
    if (source.type === 'inventory') {
      this.slots[source.index] = slot
    } else if (source.type === 'craft') {
      this.craftSlots[source.index] = slot
    } else if (source.type === 'furnace') {
      if (source.slot === 'input') {
        this.furnaceInput = slot
      } else if (source.slot === 'fuel') {
        this.furnaceFuel = slot
      } else {
        this.furnaceResult = slot
      }
    }
  }

  private bindResultDrag(element: HTMLElement) {
    element.addEventListener('dragstart', e => {
      if (!this.craftResult) {
        e.preventDefault()
        return
      }
      this.dragSource = { type: 'result' }
      e.dataTransfer?.setData('text/plain', 'craft-result')
    })
  }

  private bindFurnaceDrag(
    element: HTMLElement,
    slot: 'input' | 'fuel' | 'result'
  ) {
    element.addEventListener('dragstart', e => {
      const current = this.getSlot({ type: 'furnace', slot })
      if (!current) {
        e.preventDefault()
        return
      }
      this.dragSource = { type: 'furnace', slot }
      e.dataTransfer?.setData('text/plain', `furnace-${slot}`)
    })
    element.addEventListener('dragover', e => {
      e.preventDefault()
    })
    element.addEventListener('drop', e => {
      e.preventDefault()
      this.moveDraggedItem({ type: 'furnace', slot })
    })
  }

  private smelt() {
    if (!this.furnaceInput || !this.furnaceFuel) {
      return
    }
    const output = FURNACE_RECIPES[this.furnaceInput.itemId]
    const fuelValue = FURNACE_FUELS[this.furnaceFuel.itemId]
    if (!output || !fuelValue) {
      return
    }
    if (this.furnaceResult && this.furnaceResult.itemId !== output) {
      return
    }

    this.furnaceInput.count--
    this.furnaceFuel.count--
    if (this.furnaceInput.count <= 0) {
      this.furnaceInput = null
    }
    if (this.furnaceFuel.count <= 0) {
      this.furnaceFuel = null
    }
    if (this.furnaceResult) {
      this.furnaceResult.count = Math.min(64, this.furnaceResult.count + 1)
    } else {
      this.furnaceResult = { itemId: output, count: 1 }
    }
    this.showDrop(output, 1)
    this.renderAll()
  }

  private updateCraftResult() {
    const pattern = this.craftSlots.map(slot => slot?.itemId ?? null)
    const recipe = CRAFT_RECIPES.find(item =>
      item.pattern.every((itemId, index) => itemId === pattern[index])
    )
    this.craftResult =
      recipe && this.hasCraftIngredients() ? { ...recipe.result } : null
  }

  private takeCraftResult() {
    if (!this.craftResult) {
      return
    }

    if (!this.hasCraftIngredients()) {
      return
    }

    this.consumeCraftIngredients()

    const accepted = this.addItem(this.craftResult.itemId, this.craftResult.count)
    if (!accepted) {
      return
    }

    this.craftResult = null
    this.updateCraftResult()
    this.renderAll()
  }

  private hasCraftIngredients() {
    for (const slot of this.craftSlots) {
      if (slot && this.countItem(slot.itemId) < slot.count) {
        return false
      }
    }
    return true
  }

  private consumeCraftIngredients() {
    for (const slot of this.craftSlots) {
      if (slot) {
        this.removeItem(slot.itemId, slot.count)
      }
    }
  }

  private countItem(itemId: ItemId) {
    return this.slots.reduce((total, slot) => {
      return total + (slot?.itemId === itemId ? slot.count : 0)
    }, 0)
  }

  private removeItem(itemId: ItemId, count: number) {
    for (let i = 0; i < this.slots.length; i++) {
      const slot = this.slots[i]
      if (!slot || slot.itemId !== itemId) {
        continue
      }

      const removed = Math.min(slot.count, count)
      slot.count -= removed
      count -= removed
      if (slot.count <= 0) {
        this.slots[i] = null
      }
      if (count === 0) {
        return
      }
    }
  }
}
